<script setup lang="ts">
import {computed, getCurrentInstance, h, nextTick, onMounted, PropType, ref, type VNode, watch} from 'vue';
import {CascaderOption, ElDialog, ElMessage, ElMessageBox} from 'element-plus';
import {CabOrganization, CabTheme, CabUser, OrgThemeCombi, Role, type SessieTelling, toCab, uuidOf, type WelkomStatus} from 'common';
import {
    createObject,
    deleteObject,
    getCurrentUser,
    listObjects,
    readObject,
    sessieOverzicht,
    syncUsers,
    updateObject,
    welkomStatus,
} from '@/base/callBackend';
import UserStrip, {USER_COLUMNS} from '@/views/admin/UserStrip.vue';
import AuthLogPanel from '@/views/admin/AuthLogPanel.vue';
import GeneralFilter from '@/views/general/GeneralFilter.vue';
import PageHeader from '@/views/ui/PageHeader.vue';
import CardPanel from '@/views/ui/CardPanel.vue';
import RowGrid from '@/views/ui/RowGrid.vue';
import IconButton from '@/views/ui/IconButton.vue';
import {ChevronDown, ChevronUp, Gem, Info, Mail, Plus} from 'lucide-vue-next';

// the mail icon with the small gem the "nieuw" pill carries: the mails go to the users that still wear it
const MailNieuw = () => h('span', {class: 'relative inline-flex'}, [
    h(Mail, {size: 16, strokeWidth: 2}),
    h(Gem, {size: 9, strokeWidth: 2.5, class: 'absolute -right-1.5 -top-1.5'}),
]);
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import {ROLE_DESCRIPTIONS, USER_CHANGE_EMIT, USER_REMOVE_EMIT, USER_SESSIONS_ENDED_EMIT} from '@/base/cabConstants';
import {roleLabel} from '@/base/utils';

const users           = ref<CabUser[]>([]);
const organizations   = ref<CabOrganization[]>([]);
const filterActive    = ref<boolean>(false);
const filteredUsers   = ref<CabUser[]>([]);
const orgThemeOptions = ref<CascaderOption[]>([]);
const me              = ref<CabUser>();
const roleInfoVisible = ref<boolean>(false);
const tellingen       = ref<Record<string, SessieTelling>>({});
const welkom          = ref<Record<string, WelkomStatus>>({});

const props = defineProps({
    selectableRoles: {
        type    : Object as PropType<Role[]>,
        required: false,
    },
});

const infoRoles = computed<Role[]>(() => props.selectableRoles ?? Object.values(Role));
const isAdmin   = computed<boolean>(() => me.value?.roles.includes(Role.admin) ?? false);

// the list sorts on any column; the value a column sorts on, per column
type SorteerKolom = 'naam' | 'email' | 'rol' | 'instelling';

const KOLOM_LABEL: Record<SorteerKolom, string> = {
    naam      : 'naam',
    email     : 'e-mail',
    rol       : 'rol',
    instelling: 'instelling / thema',
};

const sorteerKolom = ref<SorteerKolom>('naam');
const sorteerOp    = ref<boolean>(true); // ascending

// the order is fixed until the members of the list or the sort settings change, not on every keystroke: a row
// that moved while being typed in would lose the focus (the browser drops it when a focused element is moved)
const volgorde     = ref<string[]>([]);
const ledenSleutel = computed<string>(() => filteredUsers.value.map((u: CabUser) => uuidOf(u)).join());
const sortedUsers  = computed<CabUser[]>(() => {
    const perUuid: Map<string, CabUser> = new Map(filteredUsers.value.map((u: CabUser) => [uuidOf(u), u]));
    return volgorde.value.map((uuid: string) => perUuid.get(uuid)).filter((u): u is CabUser => u !== undefined);
});

watch([ledenSleutel, sorteerKolom, sorteerOp], () => {
    const richting: number = sorteerOp.value ? 1 : -1;
    volgorde.value         = [...filteredUsers.value]
        .sort((a: CabUser, b: CabUser) => richting * sorteerWaarde(a).localeCompare(sorteerWaarde(b), 'nl', {sensitivity: 'base'}))
        .map((u: CabUser) => uuidOf(u));
}, {immediate: true});

function sorteerWaarde(user: CabUser): string {
    switch (sorteerKolom.value) {
        case 'naam':
            return user.name ?? '';
        case 'email':
            return user.email ?? '';
        case 'rol':
            return user.roles[0] ? roleLabel(user.roles[0]) : '';
        case 'instelling':
            return user.organizations.map((o: CabOrganization) => o.name).join(', ');
    }
}

function sorteer(kolom: SorteerKolom) {
    if (sorteerKolom.value === kolom) {
        sorteerOp.value = !sorteerOp.value;
    } else {
        sorteerKolom.value = kolom;
        sorteerOp.value    = true;
    }
}

onMounted(async () => {
    me.value              = toCab(await getCurrentUser());
    users.value           = await listObjects(CabUser);
    users.value           = users.value.filter(user => !user.email || ![me.value!.email!, 'admin@openwalnoot.com'].includes(user.email)); //Never modify yourself or main admin
    organizations.value   = await listObjects(CabOrganization);
    orgThemeOptions.value = await makeCascaderOptions();
    await loadTellingen();
});

async function loadTellingen() {
    tellingen.value = await sessieOverzicht();
    await loadWelkom();
}

// the welcome status feeds the gem in the details pill; it changes with every edit (role, e-mail, organisation)
async function loadWelkom() {
    welkom.value = await welkomStatus();
}

// the e-mails the welcome-mail button would mail right now
const kandidaten = computed<string[]>(() => Object.entries(welkom.value).filter(([, s]) => s === 'kandidaat').map(([email]) => email));

//=====================================================================================
async function onUserRemove(user: CabUser) {
    await deleteObject(user);
    users.value = users.value.filter((u) => uuidOf(u) !== uuidOf(user));
}

async function onUserAdd() {
    filterActive.value = false;
    const newUser      = new CabUser();
    if (props.selectableRoles) {
        newUser.roles = [props.selectableRoles[0]];
    }
    newUser.organizations = me.value!.organizations;
    newUser.themes        = me.value!.themes;
    if (!await createObject(newUser)) {
        return; // createObject already showed why it failed
    }
    users.value = [newUser, ... users.value];
    await nextTick();
    getUserStrip(makeStripRef(newUser))?.firstEdit();
}

async function onUserChange(user: CabUser) {
    await updateObject(user);
    users.value = users.value.map((u) =>
        uuidOf(u) === uuidOf(user) ? user : u,
    );
    await loadWelkom();
}

const instance = getCurrentInstance();

function getUserStrip(ref: string): InstanceType<typeof UserStrip> | undefined {
    return (instance?.proxy?.$refs[ref] as any)?.[0];
}

function makeStripRef(x: CabUser) {
    return `UserStrip-${uuidOf(x)}`;
}

async function onUserSync() {
    filterActive.value = false;
    let result;
    try {
        await loadWelkom();
        const emails: string[] = kandidaten.value;
        if (emails.length === 0) {
            ElMessage.info('Niemand komt in aanmerking: iedereen heeft al een wachtwoord of een open welkomstmail, of het account is nog niet compleet (rol, instelling/thema, e-mail)');
            return;
        }
        try {
            // one address per line; a long list scrolls inside the dialog
            const lijst: VNode = h('div', {class: 'max-h-60 overflow-y-auto'}, emails.map(email => h('div', email)));
            await ElMessageBox.confirm(lijst, `Welkomstmail sturen aan ${emails.length} gebruiker${emails.length === 1 ? '' : 's'}?`, {
                confirmButtonText: 'Versturen',
                cancelButtonText : 'Stop',
                type             : 'warning',
            });
        } catch (_) {
            return; // cancelled
        }
        result = await syncUsers();
    } catch (error) {
        ElMessage.error('Het versturen van de welkomstmails is mislukt');
        return;
    }
    await loadTellingen();

    if (result?.errors?.length) {
        result.errors.forEach((message: string) => ElMessage.error({
            message,
            showClose: true,
            duration : 0,
        }));
    } else {
        ElMessage({
            message: result.added.length === 0 ? 'Iedereen heeft al een wachtwoord of een open welkomstmail' : `Welkomstmail verstuurd naar ${result.added.length} gebruiker(s)`,
            type   : 'success',
        });
    }
}

async function makeCascaderOptions(): Promise<CascaderOption[]> {
    if (me.value!.roles.includes(Role.admin)) {
        return organizations.value.map((o) => ({
            label   : o.name,
            value   : uuidOf(o),
            children: o.themes.map((t) => ({
                label: t.name,
                value: uuidOf(t),
            })),
        }));
    } else {
        // Get allowed org/theme pairs for this user
        const allowedCombis: OrgThemeCombi[] = me.value!.getOrgThemeCombis();

        // Group allowed themes by organization UUID
        const orgMap: Record<string, { org: CabOrganization; themes: CabTheme[] }> = {};
        for (let {
            organization,
            theme
        } of allowedCombis) {
            organization          = await readObject(organization);
            theme                 = await readObject(theme);
            const orgUuid: string = uuidOf(organization);
            if (!orgMap[orgUuid]) {
                orgMap[orgUuid] = {
                    org   : organization,
                    themes: [],
                };
            }
            orgMap[orgUuid].themes.push(theme);
        }

        // Build cascader options
        return Object.values(orgMap).map(({
            org,
            themes,
        }) => ({
            label   : org.name,
            value   : uuidOf(org),
            children: themes.map((t) => ({
                label: t.name,
                value: uuidOf(t),
            })),
        }));
    }
}

//=====================================================================================
</script>

<template>
    <div class="flex flex-col">
        <PageHeader title="Gebruikers" :context="`${filteredUsers.length} van ${users.length}`">
            <template #before>
                <IconButton :icon="Plus" label="nieuwe gebruiker" variant="primary" data-testid="plus-button" @click="onUserAdd()"/>
            </template>
            <template #actions>
                <GeneralFilter v-model:active="filterActive"
                               v-model:cabList="users"
                               v-model:cabListFiltered="filteredUsers"
                               :id="'UserList'"/>
            </template>
        </PageHeader>

        <CardPanel min-width="64rem">
            <RowGrid v-if="filteredUsers.length" :columns="USER_COLUMNS" header>
                <span/>
                <!-- the labels start where the text in the rows starts: 8px into an inline field, 11px into an Element Plus select -->
                <span v-for="kolom in (Object.keys(KOLOM_LABEL) as SorteerKolom[])" :key="kolom" class="flex items-center gap-1" :class="kolom === 'rol' || kolom === 'instelling' ? 'pl-[11px]' : 'pl-2'">
                    <button
                        type="button"
                        class="flex items-center gap-1 cursor-pointer hover:text-zzc-antraciet"
                        :title="`sorteren op ${KOLOM_LABEL[kolom]}`"
                        @click="sorteer(kolom)"
                    >
                        {{ KOLOM_LABEL[kolom] }}
                        <component v-if="sorteerKolom === kolom" :is="sorteerOp ? ChevronUp : ChevronDown" :size="12" :stroke-width="2"/>
                    </button>
                    <IconButton v-if="kolom === 'rol'" :icon="Info" label="rollen en toegang" size="sm" @click="roleInfoVisible = true"/>
                </span>
                <!-- the mail-all button lives with the gems it acts on; one candidate is handled from their own dialog -->
                <span class="flex items-center gap-1 pl-1">
                    details
                    <IconButton v-if="kandidaten.length > 1" :icon="MailNieuw" label="welkomstmails sturen aan alle nieuwe gebruikers" size="sm" data-testid="sync-button" @click="onUserSync()"/>
                </span>
                <span/>
            </RowGrid>
            <div v-else class="px-4 py-6 text-sm text-zzc-muted">geen gebruikers</div>
            <UserStrip
                v-for="user in sortedUsers"
                :key="uuidOf(user)"
                :user="user"
                :allOrganizations="organizations"
                :selectableRoles="props.selectableRoles"
                :orgThemeOptions="orgThemeOptions"
                :me="me!"
                :allUsers="users"
                :telling="tellingen[user.email?.toLowerCase() ?? '']"
                :welkom="welkom[user.email?.toLowerCase() ?? '']"
                :ref="makeStripRef(user)"
                v-on:[USER_REMOVE_EMIT]="onUserRemove"
                v-on:[USER_CHANGE_EMIT]="onUserChange"
                v-on:[USER_SESSIONS_ENDED_EMIT]="loadTellingen"
            />
        </CardPanel>

        <AuthLogPanel v-if="isAdmin"/>
    </div>

    <el-dialog
        v-model="roleInfoVisible"
        title="Rollen en toegang"
        width="640px"
        :close-on-press-escape="true"
        :close-on-click-modal="true"
    >
        <table class="w-full text-sm">
            <tbody>
            <tr v-for="role in infoRoles" :key="role" class="border-b border-zzc-rand last:border-b-0">
                <td class="py-2 pr-4 align-top whitespace-nowrap font-semibold">
                    {{ roleLabel(role) }}
                </td>
                <td class="py-2 align-top text-zzc-antraciet">
                    {{ ROLE_DESCRIPTIONS[role] }}
                </td>
            </tr>
            </tbody>
        </table>
    </el-dialog>
</template>
